'use client';

import Pusher, { Options } from 'pusher-js';
import React, { useEffect, useMemo, useState } from 'react';
import { PusherChannelProvider } from './PusherChannelProvider';
import { PusherEventListener } from './PusherEventListener';
import { useSession } from '@hooks/index';

const pusherConfig = {
  clientKey: process.env.NEXT_PUBLIC_PUSHER_KEY!,
  cluster: process.env.NEXT_PUBLIC_PUSHER_CLUSTER!,
  userAuthentication: {
    endpoint: '/api/pusher/auth',
    transport: 'ajax' as const
  }
};

export interface PusherContextValues {
  client?: Pusher;
  triggerEndpoint?: string;
}

export const PusherCtx = React.createContext<PusherContextValues>({});

/**
 * Provider that creates your pusher instance and provides it to child hooks throughout your app.
 * Note, you can pass in value={{}} as a prop if you'd like to override the pusher client passed.
 * This is handy when simulating pusher locally, or for testing.
 * @param props Config for Pusher client
 */

interface ComponentProps {
  children: React.ReactNode;
}

export const PusherProvider: React.FC<ComponentProps> = ({ children }) => {
  const { clientKey, cluster, ...options } = pusherConfig;

  const [client, setClient] = useState<Pusher | undefined>();
  const { isLoggedIn } = useSession();
  const config: Options = useMemo(() => ({ cluster, ...options }), [cluster, options]);

  // Create pusher instnace
  useEffect(() => {
    if (!clientKey || client || !isLoggedIn) {
      return;
    }
    const pusher = new Pusher(clientKey, config);
    setClient(pusher);
  }, [client, clientKey, config, isLoggedIn]);

  // Sign in user
  useEffect(() => {
    if (client && !client.user.signin_requested) {
      client.signin();
    }
  }, [client]);

  return (
    <PusherCtx.Provider
      value={{
        client
      }}
    >
      <PusherChannelProvider>
        {/* Global event listeners – available on every page */}
        <PusherEventListener />
        {children}
      </PusherChannelProvider>
    </PusherCtx.Provider>
  );
};
